import { api } from '../../lib/api.ts';
import type { Role } from '../../store/auth.ts';

export type YaumiyyaStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED';

// `time` = arrival time (HH:MM) of an attended councilor — captured in the
// editor, shown in the haaziree (attendance register). Optional.
// `mode` = how they attended: PHYSICAL (in person) or ONLINE. Optional.
export type AttendanceMode = 'PHYSICAL' | 'ONLINE';
export interface AttendedRow { name: string; role: string; time?: string; mode?: AttendanceMode }
export interface AbsentRow { name: string; role: string; reason?: string }
export interface SecretariatRow { name: string; role: string }
export interface ObserverRow { name: string; address?: string; role?: string }
export interface Attendance {
  attended: AttendedRow[];
  absent: AbsentRow[];
  secretariat: SecretariatRow[];
  observers: ObserverRow[];
}
export type VoteChoice = 'FOR' | 'AGAINST';
export interface VoteEntry { userId: string; name: string; role?: string; vote: VoteChoice }
// One ހުށަހެޅުން (proposal) on an agenda item: proposer → vote → decision.
// An item may carry several; each is voted and decided independently.
export interface Proposal {
  text?: string;         // ހުށަހެޅުން — the proposal wording itself
  proposedBy?: string;
  secondedBy?: string;   // legacy single-proposal notes only; never set on new ones
  votesFor?: string[];
  votesAgainst?: string[];
  votes?: VoteEntry[];
  decision?: string;
}
export interface NoteEntry {
  discussion?: string;
  proposals?: Proposal[];
  // Legacy single-proposal fields, kept so old records hydrate. The editor
  // migrates them into `proposals[0]` on load; new saves write `proposals`.
  decision?: string;
  proposedBy?: string;
  secondedBy?: string;
  votesFor?: string[];
  votesAgainst?: string[];
  votes?: VoteEntry[];
}
export type NotesMap = Record<string, NoteEntry>;
export interface Details {
  chair?: string;
  startedTime?: string;
  interruptedTime?: string;
  resumedTime?: string;
  endedTime?: string;
  openingText?: string;
  closingText?: string;
}

export interface ApproverRow { id: string; name: string; role: string; approved: boolean }
export interface ApprovalRow { name: string; role: string; approvedAt: string }
export interface YaumiyyaDocRow { id: string; finalized: boolean; generatedAt: string }

export interface YaumiyyaInfo {
  id: string;
  status: YaumiyyaStatus;
  // Whether the meeting met quorum. When false, no per-item discussion/voting is
  // recorded — the yaumiyya is an attendance-only record.
  quorumMet: boolean;
  attendance: Attendance;
  notes: NotesMap;
  details: Details;
  yaumiyyaNumber: number | null;
  submittedAt: string | null;
  approvedAt: string | null;
  // WapSign document id once submitted for signing (null until the sign job
  // creates the document). The editor is at /documents/<signDocId>/edit.
  signDocId: string | null;
  requiredApprovers: ApproverRow[];
  approvals: ApprovalRow[];
  documents: YaumiyyaDocRow[];
  // Latest council-decisions (ninmun) document, once one has been generated
  // (after the yaumiyya is signed). null until then.
  ninmunDocument?: { id: string; generatedAt: string } | null;
  // WapSign signing of the ninmun by the acting president — mirrors the agenda's
  // agendaSignDocId/agendaPassedAt. null until sent / passed.
  ninmunSignDocId?: string | null;
  ninmunPassedAt?: string | null;
  // Latest attendance-register (haaziree) document, once generated (after the
  // yaumiyya is signed). Signed independently by the acting president.
  haazireeDocument?: { id: string; generatedAt: string } | null;
  haazireeSignDocId?: string | null;
  haazireePassedAt?: string | null;
}

export interface CouncilMember { id: string; name: string; nameDv?: string | null; role: Role; designationDv: string | null; designationEn: string | null }

export const yaumiyyaApi = {
  get: (meetingId: string) =>
    api.get<{ yaumiyya: YaumiyyaInfo | null }>(`/meetings/${meetingId}/yaumiyya`).then((r) => r.data.yaumiyya),
  save: (meetingId: string, body: { attendance: Attendance; notes: NotesMap; details: Details; quorumMet: boolean }) =>
    api.put(`/meetings/${meetingId}/yaumiyya`, body),
  submit: (meetingId: string) =>
    api.post(`/meetings/${meetingId}/yaumiyya/submit`),
  withdraw: (meetingId: string) =>
    api.post(`/meetings/${meetingId}/yaumiyya/withdraw`),
  reopen: (meetingId: string) =>
    api.post(`/meetings/${meetingId}/yaumiyya/reopen`),
  regenerate: (meetingId: string) =>
    api.post(`/meetings/${meetingId}/yaumiyya/documents/regenerate`),
  sendNinmunForSigning: (meetingId: string) =>
    api.post<{ signDocId: string; editorPath: string }>(`/meetings/${meetingId}/ninmun/send-for-signing`).then((r) => r.data),
  sendHaazireeForSigning: (meetingId: string) =>
    api.post<{ signDocId: string; editorPath: string }>(`/meetings/${meetingId}/haaziree/send-for-signing`).then((r) => r.data),
  documentUrl: (id: string) => `/api/yaumiyya-documents/${id}/file`,
  ninmunDocumentUrl: (id: string) => `/api/ninmun-documents/${id}/file`,
  haazireeDocumentUrl: (id: string) => `/api/haaziree-documents/${id}/file`,
  // Meeting-scoped: always serves the LATEST haaziree, so a stale cached doc id
  // can't 404. Preferred for the download button.
  haazireeFileUrl: (meetingId: string) => `/api/meetings/${meetingId}/haaziree/file`,
  // Body-aware: returns the body's elected members + shared SG/Bureau. Defaults
  // to COUNCIL on the server when body is omitted.
  councilMembers: (body?: string) =>
    api.get<{ members: CouncilMember[] }>('/org/council-members', { params: { body } }).then((r) => r.data.members),
  async generateDiscussion(meetingId: string, itemId: string): Promise<string> {
    const { data } = await api.post<{ discussion: string }>(`/meetings/${meetingId}/items/${itemId}/generate-discussion`, {});
    return data.discussion;
  },
};
